import React, { useState, useMemo } from "react";
import { Bell, Check, Clock, RotateCcw, AlertTriangle, AlertOctagon, FileQuestion, CalendarX, ChevronDown, TrendingDown, TrendingUp, Trophy, Plane, Wrench, Package, UserCog, Search, Mail, MessageSquare, ArrowUpRight } from "lucide-react";
import { useNotifications } from "../lib/AlertContext.jsx";
import { useNavigate } from "react-router-dom";
import { getCurrentUser } from "../lib/auth.js";
import { apiFetch } from "../lib/api.js";
import { formatDate } from "../lib/format.js";
import { toast } from "./Toast.jsx";
import {
  KartaPrehledu, HlavickaDlazdice, Pilulka,
} from "./PrehledDlazdice.jsx";

// Datum + čas vzniku notifikace. Systémové chyby nesou ISO timestamp (s časem),
// business notifikace už lidský formát "D.M.YYYY HH:MM". Sjednotí oboje na "D.M.YYYY HH:MM".
function fmtVytvoreno(v) {
  if (!v) return "";
  const s = String(v);
  if (s.includes("T") || /\d{4}-\d{2}-\d{2}/.test(s)) {
    const d = new Date(s);
    if (!isNaN(d.getTime())) {
      const p = (n) => String(n).padStart(2, "0");
      return `${d.getDate()}.${d.getMonth() + 1}.${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
    }
  }
  return s;
}

// Barvy a ikony podle typu
const TYPE_META = {
  undecided: {
    label: "Nerozhodnutí",
    bg: "bg-red-50", border: "border-red-300", text: "text-red-900", textSoft: "text-red-600",
    Icon: AlertTriangle,
  },
  missing_invoice: {
    label: "Chybí faktura",
    bg: "bg-amber-50", border: "border-amber-300", text: "text-amber-900", textSoft: "text-amber-600",
    Icon: FileQuestion,
  },
  missing_reservio: {
    label: "Chybí termín",
    bg: "bg-sky-50", border: "border-sky-300", text: "text-sky-900", textSoft: "text-sky-700",
    Icon: CalendarX,
  },
  roi_low: {
    label: "Nízké ROI",
    bg: "bg-rose-50", border: "border-rose-300", text: "text-rose-900", textSoft: "text-rose-700",
    Icon: TrendingDown,
  },
  cpl_high: {
    label: "Vysoký CPL",
    bg: "bg-orange-50", border: "border-orange-300", text: "text-orange-900", textSoft: "text-orange-700",
    Icon: TrendingUp,
  },
  absence_trener: {
    label: "Absence trenéra",
    bg: "bg-sky-50", border: "border-sky-300", text: "text-sky-900", textSoft: "text-sky-700",
    Icon: Plane,
  },
  milestone: {
    label: "Milník",
    bg: "bg-emerald-50", border: "border-emerald-300", text: "text-emerald-900", textSoft: "text-emerald-700",
    Icon: Trophy,
  },
  // @zmínka v komentáři úkolu (27.8.2026). Archivace = „přečteno".
  zminka: {
    label: "Zmínka v úkolu",
    bg: "bg-violet-50", border: "border-violet-300", text: "text-violet-900", textSoft: "text-violet-700",
    Icon: MessageSquare,
  },
  // === Systémové chyby v automatizaci (zdroj: System Errors sheet, mapped backendem) ===
  // Severity → typ. Owner / manager má historii všech tech chyb na jednom místě s byznys notifikacemi.
  system_error_critical: {
    label: "Kritická chyba",
    bg: "bg-red-50", border: "border-red-300", text: "text-red-900", textSoft: "text-red-700",
    Icon: AlertOctagon,
  },
  system_error_error: {
    label: "Chyba systému",
    bg: "bg-orange-50", border: "border-orange-300", text: "text-orange-900", textSoft: "text-orange-700",
    Icon: Wrench,
  },
  system_error_warn: {
    label: "Varování",
    bg: "bg-amber-50", border: "border-amber-300", text: "text-amber-900", textSoft: "text-amber-700",
    Icon: AlertTriangle,
  },
  // Předrezervace po proběhlém trialu — management musí potvrdit/zamítnout
  preliminary_to_confirm: {
    label: "Potvrdit po trialu",
    bg: "bg-blue-50", border: "border-blue-300", text: "text-blue-900", textSoft: "text-blue-700",
    Icon: Clock,
  },
  // Změna kontaktů klienta — owner + csm musí ručně doplnit do Reservio + FAPI (Modul 2A1)
  kontakt_changed: {
    label: "Změna kontaktu",
    bg: "bg-blue-50", border: "border-blue-300", text: "text-blue-900", textSoft: "text-blue-700",
    Icon: AlertTriangle,
  },
  // Změna balíčku klienta — owner + csm musí ručně upravit fakturaci v FAPI (Modul 2B)
  // Amber = peněžní/fakturační dopad, výraznější než modrý kontakt.
  balicek_changed: {
    label: "Změna balíčku",
    bg: "bg-amber-50", border: "border-amber-300", text: "text-amber-900", textSoft: "text-amber-700",
    Icon: Package,
  },
  // Generic změna karty klienta (Trenér, Lokalita, Začátek/Konec, Typ spolupráce…) (Modul 2B)
  klient_changed: {
    label: "Změna karty klienta",
    bg: "bg-slate-50", border: "border-slate-300", text: "text-slate-800", textSoft: "text-slate-600",
    Icon: UserCog,
  },
  // Nový email od klienta — generovan cron-em gmailWatchClients (15min poll)
  client_email_new: {
    label: "Nový email",
    bg: "bg-plum-50", border: "border-plum-300", text: "text-plum-900", textSoft: "text-plum-700",
    Icon: Mail,
  },
  // Příchozí SMS odpověď — generovaná webhookem sms-inbound (BulkGate)
  client_sms_reply: {
    label: "SMS odpověď",
    bg: "bg-teal-50", border: "border-teal-300", text: "text-teal-900", textSoft: "text-teal-700",
    Icon: MessageSquare,
  },
  // Výživa (12.7.2026) — komentáře/žádosti/volno z masterplánu v portálu.
  masterplan_poznamka: {
    label: "Komentář v masterplánu",
    bg: "bg-emerald-50", border: "border-emerald-300", text: "text-emerald-900", textSoft: "text-emerald-700",
    Icon: MessageSquare,
  },
  masterplan_zadost: {
    label: "Žádost o změnu plánu",
    bg: "bg-plum-50", border: "border-plum-300", text: "text-plum-900", textSoft: "text-plum-700",
    Icon: MessageSquare,
  },
  masterplan_volno: {
    label: "Volno od plánu",
    bg: "bg-sky-50", border: "border-sky-300", text: "text-sky-900", textSoft: "text-sky-700",
    Icon: Plane,
  },
  // FW-BOTI (22. 10. 2026): token AI bota za 14 dní vyprší (jen owner).
  bot_token_vyprsi: {
    label: "Token AI bota",
    bg: "bg-amber-50", border: "border-amber-300", text: "text-amber-900", textSoft: "text-amber-700",
    Icon: Clock,
  },
  // FW-BOTI (25. 10. 2026): Admin Aneta čeká na potvrzení fakturace, kód je v textu (jen owner).
  bot_potvrzeni: {
    label: "Potvrzení pro Anetu",
    bg: "bg-amber-50", border: "border-amber-300", text: "text-amber-900", textSoft: "text-amber-700",
    Icon: Clock,
  },
  // Nove hlaseni z formulare Zpetna vazba (bug / navrh / zpetna vazba).
  // Bez teto vizitky padalo na fallback "Nerozhodnuti" s cervenym error stylem.
  feedback_new: {
    label: "Zpětná vazba",
    bg: "bg-brand-50", border: "border-brand-300", text: "text-brand-900", textSoft: "text-brand-700",
    Icon: MessageSquare,
  },
  // Novy prispevek klientky v Deniku uspechu (12.8.2026) — tym muze hned
  // zareagovat srdickem nebo komentarem v sekci Denik uspechu.
  denik_prispevek: {
    label: "Deník úspěchů",
    bg: "bg-amber-50", border: "border-amber-300", text: "text-amber-900", textSoft: "text-amber-700",
    Icon: Trophy,
  },
};

// Barvu typu nese jen tahle pilulka. Řádek notifikace zůstává bílý jako
// všude jinde v aplikaci, takže seznam nevypadá jako duha.
function TypeBadge({ typ }) {
  const meta = TYPE_META[typ] || TYPE_META.undecided;
  return (
    <span className={`inline-flex items-center gap-1 rounded-lg px-2 py-0.5 text-xs font-semibold ${meta.bg} ${meta.text}`}>
      <meta.Icon className="h-3 w-3" /> {meta.label}
    </span>
  );
}

function NotificationCard({ n, onArchive, onSnooze, onRestore, selected, onToggleSelect }) {
  const navigate = useNavigate();
  const meta = TYPE_META[n.typ] || TYPE_META.undecided;
  const [snoozeOpen, setSnoozeOpen] = useState(false);
  // Optimistická aktualizace v AlertContext — žádné busy blokování, kliknutí instant
  const handle = (fn) => { fn(); };

  // Předrezervace — parse ID "prelim_{pilot}_{dow}_{hour}_{trialDate}" pro confirm/reject akce
  const isPreliminary = n.typ === "preliminary_to_confirm";
  const preliminaryParsed = useMemo(() => {
    if (!isPreliminary) return null;
    // ID format: prelim_pilotnorm_dow_hour_YYYY-MM-DD (pilotnorm má _ místo mezer)
    const m = String(n.id || "").match(/^prelim_(.+)_(\d+)_(\d+)_(\d{4}-\d{2}-\d{2})$/);
    if (!m) return null;
    return {
      pilotNorm: m[1].replace(/_/g, " "), // zpětný převod _ → mezera (jen orientační, real pilot jméno může být jinak)
      dow: parseInt(m[2], 10),
      hour: parseInt(m[3], 10),
      trialDate: m[4],
    };
  }, [isPreliminary, n.id]);

  const handlePreliminary = async (action) => {
    if (!preliminaryParsed) {
      toast("Nepodařilo se získat údaje předrezervace. Zkus prosím obnovit stránku.", "error");
      return;
    }
    // Pilot z notif klienta není přesný — backend ho hledá podle dow+hour+pilot v override sheet.
    // Ideálně bychom měli pilot v poznamce — pojďme ho extrahovat z detail textu (formát "v rozvrhu trenéra X každé Y v ZZ:00").
    const detailMatch = String(n.detail || "").match(/v rozvrhu trenéra ([^\n,]+?) každé/);
    const pilot = detailMatch ? detailMatch[1].trim() : preliminaryParsed.pilotNorm;
    try {
      const r = await apiFetch("/api/preliminary-resolve", {
        method: "POST",
        body: JSON.stringify({
          action,
          pilot,
          dow: preliminaryParsed.dow,
          hour: preliminaryParsed.hour,
          klient: n.klient,
          notifId: n.id,
        }),
      });
      if (r.ok) {
        // Optimisticky archivuj v UI (backend ji už archivoval)
        handle(() => onArchive(n.id));
      } else {
        const j = await r.json().catch(() => ({}));
        toast(`Akce selhala: ${j.error || r.status}`, "error");
      }
    } catch (e) {
      toast(`Chyba: ${e.message}`, "error");
    }
  };

  const akce = [];
  if (isPreliminary && n.stav === "aktivní") {
    akce.push(
      <button
        key="potvrdit"
        onClick={() => handlePreliminary("confirm")}
        className="btn btn-primary flex-1 sm:flex-none"
        title="Potvrdit jako pevného klienta v rozvrhu (převést na fixed)"
      >
        <Check className="h-3.5 w-3.5" /> Potvrdit pevného
      </button>,
      <button
        key="zamitnout"
        onClick={() => handlePreliminary("reject")}
        className="btn btn-secondary flex-1 sm:flex-none"
        title="Zamítnout (smazat předrezervaci, slot se vrátí na auto)"
      >
        Zamítnout
      </button>
    );
  }
  // 25. 10. 2026 (Láďa): ze zmínky v úkolu jde rovnou otevřít úkol (a zmínka se označí jako přečtená).
  const odkazUkol = n.typ === "zminka" && n.zminka && n.zminka.ukol_id && n.zminka.tabulka !== "zprava"
    ? (n.zminka.tabulka === "trener" ? `/manager/treneri/ukoly?open=${encodeURIComponent(n.zminka.ukol_id)}` : `/operations/ukoly?ukol=${encodeURIComponent(n.zminka.ukol_id)}`)
    : null;
  if (odkazUkol) {
    akce.push(
      <button key="otevrit" onClick={() => { if (n.stav === "aktivní") onArchive(n.id); navigate(odkazUkol); }} className="btn btn-secondary flex-1 sm:flex-none" title="Otevřít úkol">
        <ArrowUpRight className="h-3.5 w-3.5" /> Otevřít úkol
      </button>
    );
  }
  if (n.stav === "aktivní" && !isPreliminary) {
    akce.push(
      <button
        key="vyrizeno"
        onClick={() => handle(() => onArchive(n.id))}
        className="btn btn-primary flex-1 sm:flex-none"
        title="Označit jako vyřízené"
      >
        <Check className="h-3.5 w-3.5" /> Vyřízeno
      </button>,
      <div key="odlozit" className="relative flex-1 sm:flex-none">
        <button
          onClick={() => setSnoozeOpen((o) => !o)}
          className="btn btn-secondary w-full"
          title="Odložit na později"
        >
          <Clock className="h-3.5 w-3.5" /> Odložit
          <ChevronDown className="h-3 w-3" />
        </button>
        {snoozeOpen && (
          <div className="absolute right-0 top-full z-10 mt-1 min-w-[120px] rounded-xl border border-slate-200 bg-white shadow-lg">
            {[
              { days: 3, label: "3 dny" },
              { days: 7, label: "7 dní" },
              { days: 30, label: "30 dní" },
            ].map((opt) => (
              <button
                key={opt.days}
                onClick={() => { setSnoozeOpen(false); handle(() => onSnooze(n.id, opt.days)); }}
                className="block w-full px-3 py-2 text-left text-xs sm:hover:bg-slate-50"
              >
                {opt.label}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }
  if (n.stav === "odloženo") {
    akce.push(
      <button
        key="aktivovat"
        onClick={() => handle(() => onRestore(n.id))}
        className="btn btn-secondary flex-1 sm:flex-none"
        title="Aktivovat teď"
      >
        <RotateCcw className="h-3.5 w-3.5" /> Aktivovat teď
      </button>,
      <button
        key="vyrizeno"
        onClick={() => handle(() => onArchive(n.id))}
        className="btn btn-primary flex-1 sm:flex-none"
        title="Označit jako vyřízené"
      >
        <Check className="h-3.5 w-3.5" /> Vyřízeno
      </button>
    );
  }
  if (n.stav === "archivováno") {
    akce.push(
      <button
        key="obnovit"
        onClick={() => handle(() => onRestore(n.id))}
        className="btn btn-secondary flex-1 sm:flex-none"
        title="Obnovit (vrátit jako aktivní)"
      >
        <RotateCcw className="h-3.5 w-3.5" /> Obnovit
      </button>
    );
  }

  return (
    <div className={`py-3 ${selected ? "bg-brand-light/50" : ""}`}>
      <div className="flex items-start gap-2.5">
        {onToggleSelect && (
          <input
            type="checkbox"
            checked={!!selected}
            onChange={() => onToggleSelect(n.id)}
            className="mt-1 h-4 w-4 flex-shrink-0 cursor-pointer accent-brand"
            title="Vybrat pro hromadnou akci"
          />
        )}
        <div className="min-w-0 flex-1">
          <div className="mb-1 flex flex-wrap items-center gap-2">
            <span className="font-semibold text-slate-900 demo-sensitive">{n.klient}</span>
            <TypeBadge typ={n.typ} />
          </div>
          <div className="text-sm text-slate-600">{n.detail}</div>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {n.vytvoreno && <Pilulka>{fmtVytvoreno(n.vytvoreno)}</Pilulka>}
            {n.stav === "odloženo" && n.odlozeno_do && <Pilulka>odloženo do {n.odlozeno_do}</Pilulka>}
            {n.stav === "archivováno" && n.archivovano && <Pilulka>vyřízeno {formatDate(n.archivovano)}</Pilulka>}
          </div>
          {n.poznamka && (
            <div className="mt-1 text-xs italic text-slate-600">{'"'}{n.poznamka}{'"'}</div>
          )}
          {akce.length > 0 && (
            <div className="mt-2.5 flex gap-2">{akce}</div>
          )}
        </div>
      </div>
    </div>
  );
}

// Výživové typy — nutritionist (i owner/CSM v náhledu nutritionist) vidí JEN
// tyhle. Systémové chyby a byznys notifikace jí nepatří (14.7.2026).
const NUTRI_TYPY = new Set(["masterplan_poznamka", "masterplan_zadost", "masterplan_volno", "denik_prispevek"]);

export default function NotificationsPanel() {
  const vsechnyNotifikace = useNotifications();
  const jeNutriPohled = (getCurrentUser()?.role || "") === "nutritionist";
  const { counts: _counts, loading, error, archive, snooze, restore, bulkArchive, bulkSnooze } = vsechnyNotifikace;
  // Nutri pohled: filtr platí i pro náhled (owner/CSM) — backend tam vrací
  // feed reálné role, tak filtruje frontend. Counts přepočítat z filtru.
  const notifications = jeNutriPohled
    ? vsechnyNotifikace.notifications.filter((n) => NUTRI_TYPY.has(n.typ))
    : vsechnyNotifikace.notifications;
  const counts = jeNutriPohled
    ? notifications.reduce((c, n) => { const st = n.stav || "aktivní"; c[st] = (c[st] || 0) + 1; return c; }, { "aktivní": 0, "odloženo": 0, "archivováno": 0 })
    : _counts;
  const [tab, setTab] = useState("aktivní");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkSnoozeOpen, setBulkSnoozeOpen] = useState(false);
  const [search, setSearch] = useState("");

  // Search napříč VŠEMI poli notifikace (klient, detail, typ label, stav, poznámka).
  // Diakritika tolerantní (NFD strip), case insensitive.
  const visible = useMemo(() => {
    let list = notifications.filter((n) => n.stav === tab);
    const q = search.trim().toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
    if (!q) return list;
    return list.filter((n) => {
      const meta = TYPE_META[n.typ] || TYPE_META.undecided;
      const blob = `${n.klient || ""} ${n.detail || ""} ${meta.label || ""} ${n.typ || ""} ${n.poznamka || ""} ${n.odlozeno_do || ""}`
        .toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
      return blob.includes(q);
    });
  }, [notifications, tab, search]);
  const allSelected = visible.length > 0 && visible.every((n) => selectedIds.has(n.id));
  const someSelected = selectedIds.size > 0;

  // Přepnutí tabu vymaže výběr
  const changeTab = (key) => { setTab(key); setSelectedIds(new Set()); };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (allSelected) setSelectedIds(new Set());
    else setSelectedIds(new Set(visible.map((n) => n.id)));
  };

  const handleBulkArchive = async () => {
    const ids = [...selectedIds];
    setSelectedIds(new Set());
    await bulkArchive(ids);
  };

  const handleBulkSnooze = async (days) => {
    const ids = [...selectedIds];
    setSelectedIds(new Set());
    setBulkSnoozeOpen(false);
    await bulkSnooze(ids, days);
  };

  return (
    <KartaPrehledu>
      <HlavickaDlazdice
        ikona={Bell}
        nazev="Notifikace"
        odznak={loading ? "Načítám…" : `${counts.aktivní || 0} aktivních`}
        odznakZivy={!loading}
      />

      {/* Záložky ze vzorníku, stejné jako v detailu klienta. */}
      <div className="px-4 py-3 sm:px-5">
        <div className="flex gap-1 rounded-xl bg-slate-100 p-1">
          {[
            { key: "aktivní", label: "Aktivní", count: counts.aktivní },
            { key: "odloženo", label: "Odložené", count: counts.odloženo },
            { key: "archivováno", label: "Archiv", count: counts.archivováno },
          ].map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => changeTab(t.key)}
              className={`min-h-[44px] min-w-0 flex-1 rounded-lg px-2 py-1.5 text-sm transition sm:min-h-0 ${
                tab === t.key
                  ? "bg-white font-semibold text-slate-900 shadow-sm"
                  : "text-slate-500 sm:hover:text-slate-700"
              }`}
            >
              <span className="truncate">{t.label}</span>
              {t.count > 0 && <span className="ml-1 tabular-nums">{t.count}</span>}
            </button>
          ))}
        </div>
      </div>

      {/* Hromadný výběr */}
      {visible.length > 0 && (tab === "aktivní" || tab === "odloženo") && (
        <div className="flex flex-wrap items-center justify-between gap-2 border-y border-slate-200 bg-slate-50 px-4 py-2 sm:px-5">
          <label className="inline-flex cursor-pointer select-none items-center gap-2 text-xs font-semibold text-slate-600">
            <input
              type="checkbox"
              checked={allSelected}
              onChange={toggleSelectAll}
              className="h-4 w-4 accent-brand"
            />
            {someSelected ? `${selectedIds.size} vybráno` : "Vybrat vše"}
          </label>
          {someSelected && (
            <div className="flex flex-wrap items-center gap-1.5">
              <button onClick={handleBulkArchive} className="btn btn-primary">
                <Check className="h-3.5 w-3.5" /> Vyřízeno ({selectedIds.size})
              </button>
              <div className="relative">
                <button onClick={() => setBulkSnoozeOpen((o) => !o)} className="btn btn-secondary">
                  <Clock className="h-3.5 w-3.5" /> Odložit ({selectedIds.size})
                  <ChevronDown className="h-3 w-3" />
                </button>
                {bulkSnoozeOpen && (
                  <div className="absolute right-0 top-full z-10 mt-1 min-w-[120px] rounded-xl border border-slate-200 bg-white shadow-lg">
                    {[{ d: 3, l: "3 dny" }, { d: 7, l: "7 dní" }, { d: 30, l: "30 dní" }].map((o) => (
                      <button
                        key={o.d}
                        onClick={() => handleBulkSnooze(o.d)}
                        className="block w-full px-3 py-2 text-left text-xs sm:hover:bg-slate-50"
                      >
                        {o.l}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <button onClick={() => setSelectedIds(new Set())} className="btn btn-ghost">
                Zrušit výběr
              </button>
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="mx-4 mb-3 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700 sm:mx-5">
          Chyba při načítání notifikací: {error}
        </div>
      )}

      {/* Seznam */}
      {visible.length === 0 ? (
        <div className="px-4 py-8 text-center text-sm text-slate-500 sm:px-5">
          {tab === "aktivní" && "Žádné aktivní notifikace, vše pod kontrolou."}
          {tab === "odloženo" && "Žádné odložené notifikace."}
          {tab === "archivováno" && "Archiv je prázdný."}
        </div>
      ) : (
        <div className="divide-y divide-slate-200 px-4 sm:px-5">
          {visible.map((n) => (
            <NotificationCard
              key={n.id}
              n={n}
              onArchive={archive}
              onSnooze={snooze}
              onRestore={restore}
              selected={selectedIds.has(n.id)}
              onToggleSelect={(tab === "aktivní" || tab === "odloženo") ? toggleSelect : undefined}
            />
          ))}
        </div>
      )}
    </KartaPrehledu>
  );
}
